import { NodeSelection } from '@tiptap/pm/state';
import {
  backspaceBlockquoteAttributionStart,
  backspaceBlockquoteExtraLineAtStart,
  backspaceResourceRowExtraLineAtStart,
  backspaceResourceRowSubtitleStart,
} from './container_paragraph_keys.js';
import {
  tryUnwrapBlockquoteAtParagraphStart,
  tryUnwrapResourceTableAtParagraphStart,
} from './container_unwrap_on_backspace.js';
import {
  backspaceEmptyParagraphInListItem,
  backspaceNearMediaInList,
} from './post_editor_list_media_backspace.js';
import {
  findResourceRowDepth,
  findSubtitleChildIndex,
  isResourceRowNode,
  lastTitleChildIndex,
  mergeBackspaceFromParagraphBelow,
  isAtResourceRowTitleStart,
  resolveFirstTitleParagraphInRow,
  shouldBlockListBackspaceInResourceTable,
  unwrapResourceRowAtTitleStart,
  paragraphRowRole,
  posBeforePrevSibling,
  resourceRowHasSubtitle,
  rowChildStartPos,
} from './resource_row_paragraph.js';

function paragraphQuoteRole(paragraph) {
  return paragraph?.attrs?.psQuoteRole === 'attribution' ? 'attribution' : 'quote';
}

function pathstoneQuoteHasAttribution(blockquoteNode) {
  let found = false;
  blockquoteNode?.forEach((child) => {
    if (paragraphQuoteRole(child) === 'attribution') found = true;
  });
  return found;
}

function findAttributionChildIndex(blockquoteNode) {
  let index = -1;
  blockquoteNode.forEach((child, _offset, i) => {
    if (paragraphQuoteRole(child) === 'attribution') index = i;
  });
  return index;
}

function lastQuoteChildIndex(blockquoteNode) {
  let lastIndex = 0;
  blockquoteNode.forEach((child, _offset, index) => {
    if (paragraphQuoteRole(child) === 'quote') lastIndex = index;
  });
  return lastIndex;
}

function blockquoteChildStartPos(blockquotePos, blockquoteNode, childIndex) {
  let pos = blockquotePos + 1;
  for (let i = 0; i < childIndex; i++) {
    pos += blockquoteNode.child(i).nodeSize;
  }
  return pos;
}

function isPathstoneQuoteBlockquote(node) {
  return node?.type?.name === 'blockquote';
}

function mergeBackspaceBelowBlockquote(editor, $from, blockquoteNode, blockquotePos, childIndex) {
  const targetParagraph = blockquoteNode.child(childIndex);
  const targetStart = blockquoteChildStartPos(blockquotePos, blockquoteNode, childIndex);
  return mergeBackspaceFromParagraphBelow(editor, $from, targetStart, targetParagraph);
}

function mergeBackspaceIntoRow(editor, $from, rowNode, rowPos) {
  const targetIndex = resourceRowHasSubtitle(rowNode)
    ? findSubtitleChildIndex(rowNode)
    : lastTitleChildIndex(rowNode);
  if (targetIndex < 0) return false;
  const targetParagraph = rowNode.child(targetIndex);
  const targetStart = rowChildStartPos(rowPos, rowNode, targetIndex);
  return mergeBackspaceFromParagraphBelow(editor, $from, targetStart, targetParagraph);
}

/**
 * Single Backspace handler for the post editor. Runs from editorProps before
 * list joinBackward / liftListItem keymaps.
 */
function backspaceResourceRowNodeSelection(view, editor) {
  const { selection } = view.state;
  if (!(selection instanceof NodeSelection) || !isResourceRowNode(selection.node)) {
    return false;
  }

  const firstTitle = resolveFirstTitleParagraphInRow(selection.node, selection.from);
  if (!firstTitle) return true;

  const $atTitle = view.state.doc.resolve(firstTitle.pos + 1);
  unwrapResourceRowAtTitleStart(editor, $atTitle);
  return true;
}

export function handlePostEditorBackspace(view, event) {
  if (event.key !== 'Backspace') return false;

  const { state } = view;
  const { selection } = state;

  const editor = view.dom?.editor ?? window.currentTiptapEditor;
  if (!editor) return false;

  if (selection instanceof NodeSelection) {
    return backspaceResourceRowNodeSelection(view, editor);
  }

  if (!selection.empty) return false;

  const { $from } = selection;

  if (isAtResourceRowTitleStart($from)) {
    unwrapResourceRowAtTitleStart(editor, $from);
    return true;
  }

  if (shouldBlockListBackspaceInResourceTable($from)) {
    return true;
  }

  if ($from.parent.type.name !== 'paragraph') return false;
  if ($from.parentOffset !== 0) return false;

  const rowDepth = findResourceRowDepth($from);
  if (rowDepth >= 0 && $from.depth === rowDepth + 1) {
    if (paragraphRowRole($from.parent) === 'subtitle') {
      if (backspaceResourceRowSubtitleStart(editor, $from, rowDepth)) return true;
    }
    if (backspaceResourceRowExtraLineAtStart(editor, $from, rowDepth)) return true;
  }

  if (backspaceEmptyParagraphInListItem(editor, $from)) return true;
  if (backspaceNearMediaInList(editor, $from)) return true;

  let blockquoteDepth = -1;
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === 'blockquote') {
      blockquoteDepth = d;
      break;
    }
  }
  if (blockquoteDepth >= 0 && $from.depth === blockquoteDepth + 1) {
    if (paragraphQuoteRole($from.parent) === 'attribution') {
      if (backspaceBlockquoteAttributionStart(editor, $from, blockquoteDepth)) return true;
    }
    if (backspaceBlockquoteExtraLineAtStart(editor, $from, blockquoteDepth)) return true;
  }

  if (tryUnwrapResourceTableAtParagraphStart(view)) return true;
  if (tryUnwrapBlockquoteAtParagraphStart(view)) return true;

  if ($from.depth >= 1) {
    const parentDepth = $from.depth - 1;
    const indexInParent = $from.index(parentDepth);
    if (indexInParent > 0) {
      const parentNode = $from.node(parentDepth);
      const prevNode = parentNode.child(indexInParent - 1);

      if (isPathstoneQuoteBlockquote(prevNode)) {
        const blockquotePos = posBeforePrevSibling($from, prevNode);
        const targetIndex = pathstoneQuoteHasAttribution(prevNode)
          ? findAttributionChildIndex(prevNode)
          : lastQuoteChildIndex(prevNode);
        if (targetIndex >= 0 && mergeBackspaceBelowBlockquote(editor, $from, prevNode, blockquotePos, targetIndex)) {
          return true;
        }
      }

      if (isResourceRowNode(prevNode)) {
        const rowPos = posBeforePrevSibling($from, prevNode);
        if (mergeBackspaceIntoRow(editor, $from, prevNode, rowPos)) return true;
      }

      if (prevNode.type.name === 'resourceTable' && prevNode.childCount > 0) {
        const tablePos = posBeforePrevSibling($from, prevNode);
        let rowPos = tablePos + 1;
        const lastRow = prevNode.child(prevNode.childCount - 1);
        for (let i = 0; i < prevNode.childCount - 1; i++) {
          rowPos += prevNode.child(i).nodeSize;
        }
        if (isResourceRowNode(lastRow) && mergeBackspaceIntoRow(editor, $from, lastRow, rowPos)) {
          return true;
        }
      }
    }
  }

  return false;
}
